// Count maps: which beat is count 1, and what count any moment is in.
//
// A map is uniform (origin and beat, S1), explicit (a beat array from the tracker,
// S2) or derived (an explicit map with a count for every beat, from the event log in
// events.ts). Callers use countAt(), beatIndexAt(), timeOfBeatIndex() and fitTaps()
// and do not need to know which. Times are in seconds on the AudioContext clock (or
// file time).
//
// The beat lattice: beat i is the i-th beat of the map, for any integer i. For an
// explicit map that is index i of `beats`, extrapolated by `beat` seconds past either
// end; for a uniform map it is origin + i * beat. phrases.ts, cues.ts and evidence.ts
// all work on the lattice, so they work on every kind of map.

import { PHRASE_BEATS, type ClaveSetting } from './patterns.js';
import { medianInterval, mod } from './util.js';

export interface CountMap {
  /** Time of the beat that is count 1, in seconds. */
  readonly origin: number;
  /** Seconds per beat. For an explicit map, the median beat interval. */
  readonly beat: number;
  /** Explicit beat times, ascending. Absent: the grid is uniform at `beat` from `origin`. */
  readonly beats?: readonly number[];
  /** With `beats`: the index in `beats` of count 1. Any integer; counts repeat every PHRASE_BEATS. */
  readonly anchor?: number;
  /**
   * With `beats`: the count (1..8) of each beat, which replaces `anchor`. `deriveMap` makes it, because a
   * re-anchor or a shift changes the count from one phrase on and leaves the earlier phrases alone.
   * Past either end of `beats` the count keeps running from the nearest beat.
   */
  readonly counts?: readonly number[];
  /** The clave direction the dancer set; absent is the same as 'unknown'. */
  readonly clave?: ClaveSetting;
}

export interface TapFit {
  readonly map: CountMap;
  /** Phrase index assigned to each tap (0 for the first tap). */
  readonly phrases: readonly number[];
  /** Signed residual of each tap against the fitted map, in seconds. */
  readonly residuals: readonly number[];
  /** Largest absolute residual, in seconds. */
  readonly maxResidual: number;
}

/**
 * Fit a count map to taps made on count 1. Each tap is a count 1, so each
 * tap sits on a whole number of phrases after the first. The phrase length is
 * the shortest gap between taps (a skipped tap makes a longer gap, never a
 * shorter one). Phrase indices come from rounding, then the origin and phrase
 * length are refitted by least squares. Two taps fix the tempo exactly from
 * those two taps alone, so any jitter in them carries to the whole song.
 */
export function fitTaps(taps: readonly number[]): TapFit {
  if (taps.length < 2) throw new RangeError('fitTaps needs at least two taps');
  const sorted = [...taps].sort((a, b) => a - b);

  let phraseLength = Infinity;
  for (let i = 1; i < sorted.length; i++) {
    phraseLength = Math.min(phraseLength, sorted[i]! - sorted[i - 1]!);
  }
  if (!(phraseLength > 0)) throw new RangeError('taps must be at distinct times');

  // Round each gap on its own against the shortest gap, then accumulate the
  // phrase counts. Rounding each tap against the shortest gap would let the
  // small bias in that gap grow with the song's length.
  const phrases = [0];
  for (let i = 1; i < sorted.length; i++) {
    const gap = sorted[i]! - sorted[i - 1]!;
    phrases.push(phrases[i - 1]! + Math.max(1, Math.round(gap / phraseLength)));
  }
  const line = regress(sorted, phrases);

  const residuals = sorted.map((t, i) => t - (line.intercept + line.slope * phrases[i]!));
  return {
    map: { origin: line.intercept, beat: line.slope / PHRASE_BEATS },
    phrases,
    residuals,
    maxResidual: Math.max(...residuals.map(Math.abs)),
  };
}

/** Least-squares line t = intercept + slope * phrase. */
function regress(times: readonly number[], phrases: readonly number[]): { intercept: number; slope: number } {
  const n = times.length;
  let sx = 0, sy = 0, sxx = 0, sxy = 0;
  for (let i = 0; i < n; i++) {
    const x = phrases[i]!;
    const y = times[i]!;
    sx += x; sy += y; sxx += x * x; sxy += x * y;
  }
  const denom = n * sxx - sx * sx;
  if (denom === 0) throw new RangeError('taps do not span more than one phrase');
  const slope = (n * sxy - sx * sy) / denom;
  const intercept = (sy - slope * sx) / n;
  return { intercept, slope };
}

/**
 * Which beat of an explicit grid is count 1, from taps made on count 1: the residue (0..7) of the beat
 * index modulo PHRASE_BEATS that the most taps name (ties: the smaller total distance to the beats).
 * Each tap names the beat nearest to it. With no taps, 0.
 *
 * `oddParity` (S9c): 0 or 1 when the tracker knows which beats are odd counts (every second beat: index parity), else null. Count 1 is an odd count, so a tap names the nearest
 * ODD beat (a tap up to almost a beat late or early, up to a whole beat from the beats of the other parity, still names the right one), and the residue has the parity of `oddParity`.
 */
export function tapResidue(beats: readonly number[], taps: readonly number[], oddParity: 0 | 1 | null = null): number {
  if (beats.length < 2) throw new RangeError('tapResidue needs at least two beats');
  const beat = medianInterval(beats);
  const votes = new Array<number>(PHRASE_BEATS).fill(0);
  const distance = new Array<number>(PHRASE_BEATS).fill(0);
  for (const t of taps) {
    const x = indexAt(beats, beat, t);
    const i = oddParity === null ? Math.round(x) : oddParity + 2 * Math.round((x - oddParity) / 2);
    const residue = mod(i, PHRASE_BEATS);
    votes[residue]! += 1;
    distance[residue]! += Math.abs(t - timeOfIndex(beats, beat, i));
  }
  let best = 0;
  for (let r = 1; r < PHRASE_BEATS; r++) {
    if (votes[r]! > votes[best]! || (votes[r] === votes[best] && distance[r]! < distance[best]!)) best = r;
  }
  return best;
}

/**
 * Anchor an explicit beat array with taps made on count 1. Each tap names the
 * beat nearest to it; those beats must share one count modulo PHRASE_BEATS.
 * The residue with the most taps wins (ties: the smaller total distance).
 * One tap is enough; two or more only make the choice more certain.
 */
export function anchorTaps(beats: readonly number[], taps: readonly number[], oddParity: 0 | 1 | null = null): CountMap {
  if (beats.length < 2) throw new RangeError('anchorTaps needs at least two beats');
  const map: CountMap = { origin: 0, beat: medianInterval(beats), beats, anchor: tapResidue(beats, taps, oddParity) };
  return { ...map, origin: timeOfBeat(map, 0) };
}

/** Beat index since the map's count 1 (its anchor), as a real number. Negative before it. For a count of a moment use `countAt`. */
export function beatAt(map: CountMap, t: number): number {
  if (!map.beats) return (t - map.origin) / map.beat;
  return indexAt(map.beats, map.beat, t) - (map.anchor ?? 0);
}

/** Position of time t on the map's beat lattice: an integer at a beat, the fraction is how far to the next. */
export function beatIndexAt(map: CountMap, t: number): number {
  if (!map.beats) return (t - map.origin) / map.beat;
  return indexAt(map.beats, map.beat, t);
}

/** Time of lattice position x, in seconds. A fraction interpolates between two beats, so 4.5 is the "&" after the fifth beat. */
export function timeOfBeatIndex(map: CountMap, x: number): number {
  if (!map.beats) return map.origin + x * map.beat;
  const i = Math.floor(x);
  const a = timeOfIndex(map.beats, map.beat, i);
  return x === i ? a : a + (x - i) * (timeOfIndex(map.beats, map.beat, i + 1) - a);
}

/** Dancer count (1..8) of integer lattice beat i. */
export function countOfBeat(map: CountMap, i: number): number {
  const { counts } = map;
  if (counts && counts.length) {
    const last = counts.length - 1;
    if (i < 0) return mod(counts[0]! - 1 + i, PHRASE_BEATS) + 1;
    if (i > last) return mod(counts[last]! - 1 + (i - last), PHRASE_BEATS) + 1;
    return counts[i]!;
  }
  return mod(i - (map.beats ? map.anchor ?? 0 : 0), PHRASE_BEATS) + 1;
}

/** Dancer count (1..8) of the beat sounding at time t. */
export function countAt(map: CountMap, t: number): number {
  return countOfBeat(map, Math.floor(beatIndexAt(map, t)));
}

/** Move the count 1 by a whole number of beats. Positive moves it later, so the beat that was count 2 becomes count 1. */
export function shiftBeats(map: CountMap, beats: number): CountMap {
  if (!map.beats) return { ...map, origin: map.origin + beats * map.beat };
  if (map.counts) return { ...map, counts: map.counts.map((c) => mod(c - 1 - beats, PHRASE_BEATS) + 1) };
  const anchor = (map.anchor ?? 0) + beats;
  return { ...map, anchor, origin: timeOfBeat({ ...map, anchor }, 0) };
}

/** Time of beat i relative to count 1 (0 is count 1). */
function timeOfBeat(map: CountMap, i: number): number {
  if (!map.beats) return map.origin + i * map.beat;
  return timeOfIndex(map.beats, map.beat, i + (map.anchor ?? 0));
}

/** Fractional index into beats at time t (0 at beats[0]). Extrapolates by `beat` past either end. */
function indexAt(beats: readonly number[], beat: number, t: number): number {
  const n = beats.length;
  if (t < beats[0]!) return (t - beats[0]!) / beat;
  if (t >= beats[n - 1]!) return n - 1 + (t - beats[n - 1]!) / beat;
  let lo = 0, hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (beats[mid]! <= t) lo = mid; else hi = mid;
  }
  return lo + (t - beats[lo]!) / (beats[lo + 1]! - beats[lo]!);
}

/** Time of integer beat index i. Extrapolates by `beat` past either end. */
function timeOfIndex(beats: readonly number[], beat: number, i: number): number {
  const n = beats.length;
  if (i < 0) return beats[0]! + i * beat;
  if (i >= n) return beats[n - 1]! + (i - (n - 1)) * beat;
  return beats[i]!;
}
